import { cn } from "@/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";

export type AppPageWidth = "md" | "lg" | "full";
export type AppPagePadding = "sm" | "lg" | "none";

interface AppPageLayoutProps extends Omit<HTMLAttributes<HTMLElement>, "children"> {
    children: ReactNode;
    /** md max-w-xl · lg max-w-4xl · full unconstrained. Defaults to md. */
    width?: AppPageWidth;
    /**
     * sm - tight padding
     * lg - roomier padding
     * none - width only; use when a parent already pads
     * Defaults to lg
     */
    padding?: AppPagePadding;
    /** `section` or `div` - whichever is semantically better for the page. Defaults to `section`. */
    as?: "section" | "div";
}

const WIDTH_CLASS: Record<AppPageWidth, string> = {
    md: "mx-auto w-full min-w-0 max-w-xl",
    lg: "mx-auto w-full min-w-0 max-w-4xl",
    full: "w-full min-w-0",
};

const PADDING_CLASS: Record<AppPagePadding, string> = {
    sm: "px-2 py-0",
    lg: "p-2 md:p-4",
    none: "",
};

/**
 * Shared app content shell for consistent width and padding.
 * Pair with page titles/bodies inside; do not nest padded layouts.
 */
export function AppPageLayout({
    children,
    width = "md",
    padding = "lg",
    className,
    as: Component = "section",
    ...rest
}: AppPageLayoutProps) {
    return (
        <Component className={cn(WIDTH_CLASS[width], PADDING_CLASS[padding], className)} {...rest}>
            {children}
        </Component>
    );
}
